import React from 'react';
import { Link } from 'react-router-dom';
import { useProgress } from '../context/ProgressContext';
import { ProgressBar } from '../components';
import { lessons } from '../data/lessons';
import { trophies } from '../data/trophies';
import { getNextLesson } from '../data/lessons';
import './HomePage.css';

export const HomePage: React.FC = () => {
  const { progress } = useProgress();
  
  const nextLesson = getNextLesson(progress.lessonsCompleted);
  const completedCount = progress.lessonsCompleted.length;
  const unlockedTrophies = Object.keys(progress.trophiesUnlocked).length;
  const totalTrophies = trophies.length;

  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = progress.childrenNames[0];
    const timeGreeting = hour < 12 ? 'Bună dimineața' : hour < 18 ? 'Bună ziua' : 'Bună seara';
    return name ? `${timeGreeting}, ${name}!` : `${timeGreeting}!`;
  };

  const getMotivationalMessage = () => {
    if (completedCount === 0) {
      return 'Gata să începi aventura? Prima lecție te așteaptă!';
    }
    if (completedCount < 10) {
      return 'Ai făcut primii pași! Continuă să înveți în fiecare zi.';
    }
    if (completedCount < 20) {
      return 'Ești pe drumul cel bun! Deja știi multe lucruri noi.';
    }
    if (completedCount < 30) {
      return 'Aproape ai terminat! Mai ai doar câteva zile.';
    }
    return 'Felicitări! Ai completat toate cele 30 de zile! 🎉';
  };

  return (
    <div className="home-page">
      <section className="hero-section">
        <h1 className="hero-title">
          {getGreeting()}
        </h1>
        <p className="hero-subtitle">{getMotivationalMessage()}</p>
      </section>

      <section className="stats-section">
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <span className="stat-value">{completedCount}/30</span>
            <span className="stat-label">Lecții completate</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <span className="stat-value">{unlockedTrophies}/{totalTrophies}</span>
            <span className="stat-label">Trofee deblocate</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎮</div>
          <div className="stat-content">
            <span className="stat-value">{progress.gameHighScore}</span>
            <span className="stat-label">High Score</span>
          </div>
        </div>
      </section>

      <section className="progress-section">
        <h2 className="section-title">Progresul tău</h2>
        <ProgressBar
          current={completedCount}
          total={30}
          label="Zile completate"
        />
        <div className="week-indicators">
          {[1, 2, 3, 4, 5, 6].map((week) => {
            const weekLessons = lessons.filter((l) => l.saptamana === week);
            const weekCompleted = weekLessons.filter((l) =>
              progress.lessonsCompleted.includes(l.id)
            ).length;
            const isComplete = weekCompleted === weekLessons.length;
            return (
              <div
                key={week}
                className={`week-indicator ${isComplete ? 'complete' : ''}`}
                title={`Săptămâna ${week}: ${weekCompleted}/${weekLessons.length}`}
              >
                S{week}
              </div>
            );
          })}
        </div>
      </section>

      {nextLesson && (
        <section className="next-lesson-section">
          <h2 className="section-title">Continuă cu:</h2>
          <div className="next-lesson-card">
            <div className="next-lesson-badge">Ziua {nextLesson.id}</div>
            <h3 className="next-lesson-title">{nextLesson.titlu}</h3>
            <p className="next-lesson-description">
              Săptămâna {nextLesson.saptamana} • 30 minute
            </p>
            <Link to={`/lectie/${nextLesson.id}`} className="btn btn-primary btn-lg">
              Începe Lecția
            </Link>
          </div>
        </section>
      )}

      {completedCount === 30 && (
        <section className="complete-section">
          <div className="complete-card">
            <span className="complete-icon">🎓</span>
            <h2>Felicitări, Absolvent!</h2>
            <p>
              Ai completat toate cele 30 de zile și ai devenit un adevărat
              dezvoltator web! Continuă să explorezi și să creezi.
            </p>
            <div className="complete-actions">
              <Link to="/trofee" className="btn btn-primary">
                Vezi Trofeele
              </Link>
              <Link to="/generator" className="btn btn-secondary">
                Generează Trofeu AI
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="quick-links-section">
        <h2 className="section-title">Acces Rapid</h2>
        <div className="quick-links-grid">
          <Link to="/plan" className="quick-link">
            <span className="quick-link-icon">📅</span>
            <span className="quick-link-text">Plan 30 Zile</span>
          </Link>
          <Link to="/trofee" className="quick-link">
            <span className="quick-link-icon">🏆</span>
            <span className="quick-link-text">Camera Trofeelor</span>
          </Link>
          <Link to="/joc" className="quick-link">
            <span className="quick-link-icon">🎮</span>
            <span className="quick-link-text">Mini-Joc</span>
          </Link>
          <Link to="/generator" className="quick-link">
            <span className="quick-link-icon">🤖</span>
            <span className="quick-link-text">Generator AI</span>
          </Link>
        </div>
      </section>
    </div>
  );
};
